import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, CheckCircle2, Loader2, ExternalLink, ShieldCheck, Clock } from "lucide-react";
import { api, formatApiError, gbp } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

/**
 * Retailer payout settings — Stripe Connect Express onboarding.
 * Buyers pay the full price at checkout; FloorMatch keeps its commission
 * (12% standard, 0% for founder retailers) and the rest is paid out to the
 * retailer's connected Stripe account.
 */
export default function RetailerPayouts() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  async function loadStatus() {
    setLoading(true);
    try {
      const r = await api.get("/retailer/stripe/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStatus();
    if (params.get("done")) toast.success("Thanks — we're checking your payout details.");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function startOnboarding() {
    setStarting(true);
    try {
      const r = await api.post("/retailer/stripe/connect-link", { origin_url: window.location.origin });
      if (r.data.available === false) {
        setUnavailable(true);
        return;
      }
      if (r.data.url) window.location.assign(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStarting(false);
    }
  }

  const ready = status?.charges_enabled && status?.payouts_enabled;
  const pending = status?.connected && !ready;

  return (
    <RetailerLayout>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="retailer-payouts-page">
        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#2A5A3B]/10 text-[#2A5A3B]">
            <Banknote className="w-6 h-6" />
          </span>
          <h1 className="font-display font-bold text-2xl text-slate-900">Payouts</h1>
        </div>
        <p className="text-slate-600 text-sm mb-6">
          Get paid directly into your bank. Shoppers pay the full price when they
          reserve; FloorMatch keeps its commission and the rest is paid out to you.
        </p>

        {loading ? (
          <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : unavailable ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="payouts-unavailable">
            <div className="flex items-center gap-2 text-amber-900 font-semibold"><Clock className="w-5 h-5" /> Payout setup is being switched on</div>
            <p className="text-sm text-amber-800 mt-2">
              Direct payouts aren't available on your account just yet. We'll
              email you the moment self-serve payout setup goes live — no action
              needed from you right now.
            </p>
          </div>
        ) : ready ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="payouts-ready">
            <div className="flex items-center gap-2 text-emerald-900 font-semibold">
              <CheckCircle2 className="w-5 h-5" /> You're all set up for payouts
            </div>
            <p className="text-sm text-emerald-800 mt-2">
              Payments for your sales will be transferred to your connected bank account on Stripe's standard schedule.
            </p>
            <button
              onClick={startOnboarding}
              disabled={starting}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-900 underline"
              data-testid="payouts-manage"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Update payout details
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="payouts-setup">
            {pending && (
              <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="payouts-pending">
                Your details are being reviewed by Stripe. You can finish or update them below.
              </div>
            )}
            <ul className="space-y-2 text-sm text-slate-700 mb-5">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2A5A3B]" /> Secure onboarding, hosted by Stripe</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2A5A3B]" /> Money lands straight in your bank</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2A5A3B]" /> Commission is deducted automatically — no invoices</li>
            </ul>
            <button
              onClick={startOnboarding}
              disabled={starting}
              className="fm-btn-primary inline-flex items-center gap-2 disabled:opacity-70"
              data-testid="payouts-start"
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
              {pending ? "Continue payout setup" : "Set up payouts"}
            </button>
          </div>
        )}
        <RetailerCombinedOrders />
      </main>
    </RetailerLayout>
  );
}

function RetailerCombinedOrders() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let on = true;
    api.get("/retailer/orders").then((r) => { if (on) setData(r.data); }).catch(() => { if (on) setData({ orders: [] }); });
    return () => { on = false; };
  }, []);
  if (!data || !data.orders?.length) return null;
  const stateLabel = { in_stripe_balance: ["In your Stripe balance", "bg-emerald-50 text-emerald-700"], pending: ["Transfer pending", "bg-amber-50 text-amber-700"], reversed: ["Refunded", "bg-slate-100 text-slate-500"] };
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="retailer-orders">
      <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><Banknote className="w-5 h-5 text-[#0F3D2E]" /> Multi-shop orders</h2>
      <p className="text-sm text-slate-500 mt-1">
        Your lines from combined customer orders. Funds are transferred into your Stripe balance; {data.bank_payouts_enabled ? "bank payouts are active." : "bank payouts start once you finish payout setup."}
      </p>
      <div className="mt-4 space-y-3" data-testid="retailer-orders-list">
        {data.orders.map((o, idx) => {
          const [lbl, cls] = stateLabel[o.transfer_state] || stateLabel.pending;
          return (
            <div key={idx} className="border border-slate-200 rounded-xl p-4" data-testid={`retailer-order-${o.order_reference}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono font-semibold text-slate-800">{o.order_reference}</div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{lbl}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">{o.items.map((i) => `${i.title} (${i.room_name})`).join(" · ")}</div>
              <table className="w-full text-sm mt-3">
                <tbody>
                  <tr><td className="py-1 text-slate-500">Gross sale</td><td className="py-1 text-right font-semibold">{gbp(o.gross_gbp)}</td></tr>
                  <tr><td className="py-1 text-slate-500">FloorMatch commission (before fees)</td><td className="py-1 text-right text-slate-600">− {gbp(o.commission_gbp)}</td></tr>
                  <tr className="border-t border-slate-100"><td className="py-1 text-slate-700 font-medium">Your payout</td><td className="py-1 text-right font-bold text-[#0F3D2E]">{gbp(o.payout_gbp)}</td></tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">FloorMatch pays all card/processing fees — they are not deducted from your payout. Commission is shown before fees and is not FloorMatch profit.</p>
    </div>
  );
}
